/**
 * AB#20569 — Remake Intake Questionnaire option lists.
 *
 * MIRRORS the backend enums (OrderRecordType, OrderFailureStage, OrderCulprit,
 * OrderCulpritSubcategory, OrderResponsibleTeam, OrderCostBearer,
 * OrderAttributionTarget) and OrderFailureCategory's two groups.
 *
 * The backend also serves all of this from GET api/vendor/order-failure-record-schema.
 * These constants are the offline fallback so the form still renders if that call fails —
 * the server remains the source of truth, and anything added there should be added here.
 */

export interface QuestionnaireOption {
  value: string;
  label: string;
}

export interface RecordTypeOption extends QuestionnaireOption {
  helperText: string;
  countsAsFailure: boolean;
}

/** Step 1. Asked first, because it decides whether the order counts against anyone. */
export const recordTypes: RecordTypeOption[] = [
  {
    value: 'remake',
    label: 'Remake — production failure',
    helperText: "Something went wrong on our side or the factory's. Counts toward the remake rate.",
    countsAsFailure: true,
  },
  {
    value: 'reorder',
    label: 'Reorder — customer wants more',
    helperText: 'Nothing failed. Does not count.',
    countsAsFailure: false,
  },
  {
    value: 'warranty',
    label: 'Warranty claim',
    helperText: 'Garment failed in use after delivery.',
    countsAsFailure: true,
  },
  {
    value: 'transit_loss',
    label: 'Replacement — lost or damaged in transit',
    helperText: 'Left our hands intact; the courier or customs is involved.',
    countsAsFailure: true,
  },
  {
    value: 'not_failure',
    label: 'Not a failure — other',
    helperText: 'Sample, size run, goodwill piece, internal stock.',
    countsAsFailure: false,
  },
];

/**
 * The record types that may be filed against a given ORDER type.
 *
 * The two describe the same order, so they must not contradict each other. Choosing "Remake"
 * as the order type and then answering "reorder" in the questionnaire produced an order that
 * IS a remake and counted as nothing — no error, and invisible in the remake rate. The API
 * refuses that combination now; this keeps it off the screen so nobody reaches the refusal.
 *
 * 'not_failure' survives every filter on purpose: "we are remaking it, but it was not our
 * fault" is the case the questionnaire exists for, and it already costs a written reason.
 */
export function recordTypesForOrderType(symbol?: string | null): RecordTypeOption[] {
  if (!symbol) return recordTypes;

  const isReorder = symbol === 'REORDER';
  const orderTypeCountsAsFailure = ['Ri', 'R'].includes(symbol);

  return recordTypes.filter((type) => {
    if (type.value === 'not_failure') return true;
    // Remake and Reorder are the same axis — an order cannot be both.
    if (type.value === 'reorder') return isReorder;
    // A failure may only be filed against an order type that claims to be one.
    if (type.countsAsFailure) return orderTypeCountsAsFailure;
    return true;
  });
}

/** Step 3 — the full defect-report list. Offered for every failure type except warranty. */
export const standardFailureCategories: QuestionnaireOption[] = [
  { value: 'color_mismatch', label: 'Color Mismatch' },
  { value: 'customer_change', label: 'Customer Change' },
  { value: 'damaged_soiled', label: 'Damaged / Soiled' },
  { value: 'fabric_material', label: 'Fabric / Material' },
  { value: 'logo_design', label: 'Logo / Design' },
  { value: 'print_sublimation', label: 'Print / Sublimation' },
  { value: 'quantity_short_missing', label: 'Quantity Short / Missing' },
  { value: 'sizing_fit', label: 'Sizing / Fit' },
  { value: 'stitching_construction', label: 'Stitching / Construction' },
  { value: 'wrong_product_sku', label: 'Wrong Product / SKU' },
  { value: 'other', label: 'Other' },
];

/**
 * Step 3 — the warranty list.
 *
 * Four categories plus Other, because production categories do not describe a garment that
 * failed in use. Note 'Stitch / Construction' here against 'Stitching / Construction' in
 * the standard list: the difference is the specification's, not a typo, and the two are
 * separate values so warranty and production failures stay distinguishable in reporting.
 */
export const warrantyFailureCategories: QuestionnaireOption[] = [
  { value: 'damaged', label: 'Damaged' },
  { value: 'fabric_material', label: 'Fabric / Material' },
  { value: 'stitch_construction', label: 'Stitch / Construction' },
  { value: 'sizing_fit', label: 'Sizing / Fit' },
  { value: 'other', label: 'Other' },
];

/** Step 3 — "shows where the net leaks, not just who to bill". */
export const failureStages: QuestionnaireOption[] = [
  { value: 'artwork_pre_production', label: 'Artwork / pre-production' },
  { value: 'in_production', label: 'In production' },
  { value: 'factory_qc', label: 'Factory QC' },
  { value: 'custimoo_dqc', label: 'Custimoo DQC' },
  { value: 'logistics_packing', label: 'Logistics & packing' },
  { value: 'after_shipment', label: 'After shipment — customer reported' },
];

/** Step 4. 'pending_investigation' keeps the record open instead of forcing a guess. */
export const culprits: QuestionnaireOption[] = [
  { value: 'factory', label: 'Factory' },
  { value: 'customer', label: 'Customer' },
  { value: 'merchant', label: 'Merchant' },
  { value: 'shipping_courier', label: 'Shipping courier' },
  { value: 'custimoo', label: 'Custimoo' },
  { value: 'pending_investigation', label: 'Not yet established — under investigation' },
];

/** Step 4 — which part of Custimoo. 'Administrative' is where an order-admin error belongs. */
export const culpritSubcategories: QuestionnaireOption[] = [
  { value: 'design', label: 'Design' },
  { value: 'administrative', label: 'Administrative' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'external', label: 'External' },
];

/** Shared by investigation owner (step 4) and preventive owner (step 5) — one list in the spec. */
export const responsibleTeams: QuestionnaireOption[] = [
  { value: 'order_admin', label: 'Order Admin' },
  { value: 'digital_qc', label: 'Digital QC' },
  { value: 'production', label: 'Production' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'merchant_csm', label: 'Merchant / CSM' },
  { value: 'factory', label: 'Factory' },
];

/** Step 4 — separate from blame: the factory can be at fault while we absorb the cost. */
export const costBearers: QuestionnaireOption[] = [
  { value: 'custimoo', label: 'Custimoo' },
  { value: 'factory', label: 'Factory' },
  { value: 'customer', label: 'Customer' },
  { value: 'merchant', label: 'Merchant' },
  { value: 'shared', label: 'Shared' },
  { value: 'not_yet_decided', label: 'Not yet decided' },
];

/** Step 2 — the last two route the record to the Lead Order Administrator. */
export const attributionTargets: QuestionnaireOption[] = [
  { value: 'me', label: 'Me' },
  { value: 'other', label: 'Another order admin…' },
  { value: 'unassigned', label: 'No admin was assigned to this order' },
  { value: 'departed', label: 'Admin has left the company' },
];

export const FAILURE_DESCRIPTION_MIN_LENGTH = 20;
export const ALSO_AFFECTS_PATTERN = /^\d{4,6}(\s*,\s*\d{4,6})*$/;
export const ORIGINAL_ORDER_PATTERN = /^\d{4,6}$/;

export function recordTypeCountsAsFailure(value?: string | null): boolean {
  if (!value) return false;
  return recordTypes.find((t) => t.value === value)?.countsAsFailure === true;
}

export function failureCategoriesForRecordType(recordType?: string | null): QuestionnaireOption[] {
  return recordType === 'warranty' ? warrantyFailureCategories : standardFailureCategories;
}
